import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Typography,
  Button,
  Avatar,
  Tag,
  Spin,
  Modal,
  message,
  Tooltip,
  Form,
  Input,
  Select,
  Space,
  Divider,
  Progress,
  Collapse,
} from 'antd';
import {
  LogoutOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  FireFilled,
  BookOutlined,
  SoundOutlined,
  PlayCircleOutlined,
  LoadingOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  LockOutlined,
  UserOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from '@ant-design/icons';
import { api } from '@/services/api';
import { API_ENDPOINTS, ROLE_STYLES, AVATAR_COLORS } from '@/constants';
import { useAuthStore } from '@/stores/authStore';
import { formatShortDate } from '@/utils/date';

import type { ApiResponse, User, StudentAnalytics } from '@/types';

const { Title, Text } = Typography;

function getSoundKey(userId: string | undefined) {
  return `cardwise-sound-enabled-${userId ?? 'guest'}`;
}

function getAvatarColor(avatarId: string | null | undefined, name: string): string {
  if (avatarId) {
    const idx = parseInt(avatarId, 10);
    if (!isNaN(idx) && idx >= 0 && idx < AVATAR_COLORS.length) {
      return AVATAR_COLORS[idx];
    }
  }
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user: authUser, clearAuth } = useAuthStore();
  const queryClient = useQueryClient();

  const soundKey = getSoundKey(authUser?.id);

  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [selectedAvatarIdx, setSelectedAvatarIdx] = useState<number | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem(getSoundKey(authUser?.id));
    return stored === null ? true : stored === 'true';
  });
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [newPassword, setNewPassword] = useState('');
  const audioCtxRef = useRef<AudioContext | null>(null);

  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => api.get<never, ApiResponse<User>>(API_ENDPOINTS.USERS_ME),
    staleTime: 5 * 60 * 1000,
  });

  const { data: analyticsData } = useQuery({
    queryKey: ['student', 'analytics', authUser?.id],
    queryFn: () =>
      api.get<never, ApiResponse<StudentAnalytics>>(
        `${API_ENDPOINTS.ANALYTICS_STUDENTS}/${authUser?.id}`,
      ),
    enabled: !!authUser?.id,
  });

  const profile = profileData?.data;
  const analytics = analyticsData?.data;

  // Init selected avatar from profile
  useEffect(() => {
    if (profile?.avatarId) {
      const idx = parseInt(profile.avatarId, 10);
      if (!isNaN(idx)) setSelectedAvatarIdx(idx);
    }
  }, [profile?.avatarId]);

  const updateAvatarMutation = useMutation({
    mutationFn: (avatarId: string) =>
      api.patch<never, ApiResponse<User>>(API_ENDPOINTS.USERS_ME, { avatarId }),
    onSuccess: () => {
      message.success('Avatar updated!');
      setAvatarModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
    },
    onError: () => {
      message.error('Failed to update avatar. Please try again.');
    },
  });

  const handleAvatarConfirm = () => {
    if (selectedAvatarIdx === null) return;
    updateAvatarMutation.mutate(selectedAvatarIdx.toString());
  };

  const passwordChecks = useMemo(() => ({
    length: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    digit: /\d/.test(newPassword),
  }), [newPassword]);

  const passwordStrengthScore = Object.values(passwordChecks).filter(Boolean).length;

  const updateProfileMutation = useMutation({
    mutationFn: (data: { name?: string; phone?: string; gradeIds?: number[] }) =>
      api.patch<never, ApiResponse<User>>(API_ENDPOINTS.USERS_ME, data),
    onSuccess: () => {
      message.success('Profile updated!');
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
    },
    onError: () => message.error('Failed to update profile. Please try again.'),
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.post(API_ENDPOINTS.AUTH_CHANGE_PASSWORD, data),
    onSuccess: () => {
      message.success('Password changed successfully!');
      passwordForm.resetFields();
      setNewPassword('');
    },
    onError: () => message.error('Failed to change password. Check your current password.'),
  });

  const handleStartEditing = () => {
    profileForm.setFieldsValue({
      name: profile?.name ?? '',
      email: profile?.email ?? '',
      phone: profile?.phone ?? '',
      gradeIds: profile?.gradeIds ?? [],
    });
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    profileForm.resetFields();
  };

  const handleSaveProfile = () => {
    profileForm.validateFields().then((values) => {
      updateProfileMutation.mutate({
        name: values.name,
        phone: values.phone?.trim() || undefined,
        gradeIds: values.gradeIds ?? [],
      });
    });
  };

  const handleChangePassword = () => {
    passwordForm.validateFields().then((values) => {
      changePasswordMutation.mutate({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
    });
  };

  const handleSoundToggle = (checked: boolean) => {
    setSoundEnabled(checked);
    localStorage.setItem(soundKey, String(checked));
  };

  const playPreviewSound = () => {
    if (isPlayingPreview) return;

    let ctx = audioCtxRef.current;
    if (!ctx || ctx.state === 'closed') {
      ctx = new AudioContext();
      audioCtxRef.current = ctx;
    }

    setIsPlayingPreview(true);

    const playTone = (
      startAt: number,
      frequency: number,
      gainPeak: number,
      duration: number,
      type: OscillatorType = 'sine',
    ) => {
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(frequency, startAt);
      gain.gain.setValueAtTime(0, startAt);
      gain.gain.linearRampToValueAtTime(gainPeak, startAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, startAt + duration);
      osc.start(startAt);
      osc.stop(startAt + duration);
    };

    const now = ctx.currentTime;
    // "Got It!" sound — ascending two-tone ding
    playTone(now, 523, 0.3, 0.4);        // C5
    playTone(now + 0.18, 784, 0.25, 0.5); // G5

    // Short gap, then "Need Help" sound — soft descending tone
    playTone(now + 0.85, 392, 0.2, 0.35, 'triangle'); // G4
    playTone(now + 1.1, 294, 0.15, 0.45, 'triangle'); // D4

    setTimeout(() => setIsPlayingPreview(false), 1600);
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  if (profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <Spin size="large" />
      </div>
    );
  }

  const roleStyle = ROLE_STYLES.STUDENT;
  const displayName = profile?.name ?? authUser?.name ?? 'Student';
  const avatarColor = getAvatarColor(profile?.avatarId, displayName);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          My Profile
        </Title>
        <Text type="secondary" style={{ fontSize: 15 }}>
          Manage your account and preferences.
        </Text>
      </div>

      {/* 1. Profile card */}
      <Card style={{ borderRadius: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20 }}>
          <Avatar
            size={72}
            shape="square"
            style={{
              background: avatarColor,
              fontWeight: 700,
              fontSize: 28,
              cursor: 'pointer',
              flexShrink: 0,
              borderRadius: 16,
            }}
            onClick={() => setAvatarModalOpen(true)}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              {displayName}
            </Title>
            <Tag
              style={{
                color: roleStyle.color,
                background: roleStyle.bg,
                border: 'none',
                fontWeight: 600,
                fontSize: 12,
                textTransform: 'uppercase',
              }}
            >
              Student
            </Tag>
          </div>
          {!isEditing ? (
            <Button icon={<EditOutlined />} onClick={handleStartEditing}>Edit</Button>
          ) : (
            <Space>
              <Button onClick={handleCancelEditing} icon={<CloseOutlined />}>Cancel</Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={updateProfileMutation.isPending}
                onClick={handleSaveProfile}
              >
                Save
              </Button>
            </Space>
          )}
        </div>

        {isEditing ? (
          <Form form={profileForm} layout="vertical">
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
              <Input prefix={<UserOutlined />} placeholder="Your name" />
            </Form.Item>
            <Form.Item name="email" label="Email">
              <Input prefix={<MailOutlined />} readOnly style={{ cursor: 'default', background: '#f8fafc', color: '#94a3b8' }} />
            </Form.Item>
            <Form.Item
              name="phone"
              label="Phone"
              rules={[{ pattern: /^\+?[0-9\s\-()]{7,20}$/, message: 'Enter a valid phone number' }]}
            >
              <Input prefix={<PhoneOutlined />} placeholder="e.g. +91 98765 43210" />
            </Form.Item>
            <Form.Item name="gradeIds" label="Grade">
              <Select
                mode="multiple"
                placeholder="Select your grade(s)"
                options={[
                  { value: 0, label: 'Kindergarten' },
                  ...Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: `Grade ${i + 1}` })),
                ]}
              />
            </Form.Item>
          </Form>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ color: '#94a3b8', fontSize: 16 }}><MailOutlined /></span>
              <Text type="secondary" style={{ minWidth: 100 }}>Email</Text>
              <Text>{profile?.email ?? '—'}</Text>
            </div>
            {profile?.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: '#94a3b8', fontSize: 16 }}><PhoneOutlined /></span>
                <Text type="secondary" style={{ minWidth: 100 }}>Phone</Text>
                <Text>{profile.phone}</Text>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ color: '#94a3b8', fontSize: 16 }}><CalendarOutlined /></span>
              <Text type="secondary" style={{ minWidth: 100 }}>Member Since</Text>
              <Text>{formatShortDate(profile?.createdAt)}</Text>
            </div>
            {profile?.gradeIds && profile.gradeIds.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: '#94a3b8', fontSize: 16 }}><BookOutlined /></span>
                <Text type="secondary" style={{ minWidth: 100 }}>Grade</Text>
                <Space size={[0, 4]} wrap>
                  {profile.gradeIds.map((g) => (
                    <Tag key={g}>{g === 0 ? 'Kindergarten' : `Grade ${g}`}</Tag>
                  ))}
                </Space>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* 2. Streak card */}
      {analytics && analytics.currentStreak > 0 && (
        <Card
          style={{
            borderRadius: 12,
            marginBottom: 16,
            background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
            border: '1px solid #fde68a',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <FireFilled style={{ fontSize: 36, color: '#f59e0b' }} />
            <div>
              <Text strong style={{ fontSize: 28, color: '#92400e', display: 'block', lineHeight: 1.1 }}>
                {analytics.currentStreak}
              </Text>
              <Text style={{ fontSize: 14, color: '#a16207' }}>day streak</Text>
            </div>
          </div>
        </Card>
      )}

      {/* 3. Preferences card */}
      <Card
        title={<Text strong style={{ fontSize: 16 }}>Preferences</Text>}
        style={{ borderRadius: 12, marginBottom: 16 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <SoundOutlined style={{ fontSize: 18, color: '#6366f1' }} />
            <div>
              <Text strong style={{ display: 'block', fontSize: 14 }}>Card Tap Sounds</Text>
              <Text type="secondary" style={{ fontSize: 13 }}>Short ping when marking a card — not music</Text>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Tooltip title="Preview the ping sound">
              <Button
                type="text"
                size="small"
                icon={isPlayingPreview ? <LoadingOutlined /> : <PlayCircleOutlined />}
                onClick={playPreviewSound}
                disabled={isPlayingPreview}
                style={{ color: '#6366f1', padding: '0 6px' }}
                aria-label="Preview card tap sound"
              />
            </Tooltip>
            <Button
              onClick={() => handleSoundToggle(!soundEnabled)}
              style={{
                borderRadius: 20,
                fontWeight: 600,
                fontSize: 13,
                background: soundEnabled ? '#f0fdf4' : '#fef2f2',
                color: soundEnabled ? '#16a34a' : '#dc2626',
                borderColor: soundEnabled ? '#86efac' : '#fca5a5',
              }}
            >
              {soundEnabled ? '🔔 Sound On' : '🔕 Sound Off'}
            </Button>
          </div>
        </div>
      </Card>

      {/* 4. Change password — accordion, closed by default */}
      <Collapse
        ghost
        style={{ marginBottom: 16, background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0' }}
        items={[{
          key: 'password',
          label: <Text strong style={{ fontSize: 15 }}><LockOutlined style={{ marginRight: 8 }} />Change Password</Text>,
          children: (
            <Form form={passwordForm} layout="vertical" style={{ paddingTop: 4 }}>
              <Form.Item name="currentPassword" label="Current Password" rules={[{ required: true, message: 'Required' }]}>
                <Input.Password placeholder="Enter current password" />
              </Form.Item>
              <Form.Item name="newPassword" label="New Password" rules={[{ required: true, min: 8, message: 'At least 8 characters' }]}>
                <Input.Password placeholder="Enter new password" onChange={(e) => setNewPassword(e.target.value)} />
              </Form.Item>
              {newPassword && (
                <div style={{ marginBottom: 16 }}>
                  <Progress
                    percent={passwordStrengthScore * 25}
                    showInfo={false}
                    strokeColor={passwordStrengthScore <= 1 ? '#ef4444' : passwordStrengthScore <= 2 ? '#f59e0b' : '#22c55e'}
                    size="small"
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                    {[
                      { key: 'length', label: '8+ chars' },
                      { key: 'upper', label: 'Uppercase' },
                      { key: 'lower', label: 'Lowercase' },
                      { key: 'digit', label: 'Number' },
                    ].map(({ key, label }) => (
                      <span key={key} style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                        {passwordChecks[key as keyof typeof passwordChecks]
                          ? <CheckCircleFilled style={{ color: '#22c55e' }} />
                          : <CloseCircleFilled style={{ color: '#ef4444' }} />
                        }
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Required' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm new password" />
              </Form.Item>
              <Button type="primary" loading={changePasswordMutation.isPending} onClick={handleChangePassword}>
                Change Password
              </Button>
            </Form>
          ),
        }]}
      />

      <Divider />

      {/* 5. Logout */}
      <Button
        danger
        size="large"
        icon={<LogoutOutlined />}
        block
        onClick={handleLogout}
        style={{ borderRadius: 12, height: 48, fontWeight: 600, fontSize: 15 }}
      >
        Logout
      </Button>

      {/* Avatar picker modal */}
      <Modal
        open={avatarModalOpen}
        title="Choose Your Avatar Color"
        onCancel={() => setAvatarModalOpen(false)}
        onOk={handleAvatarConfirm}
        okText="Save"
        okButtonProps={{ loading: updateAvatarMutation.isPending }}
        centered
        width={400}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 20, fontSize: 14 }}>
          Pick a color for your avatar. This will be shown to your teachers.
        </Text>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 16,
            justifyItems: 'center',
          }}
        >
          {AVATAR_COLORS.map((color, idx) => (
            <div
              key={color}
              onClick={() => setSelectedAvatarIdx(idx)}
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: color,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border:
                  selectedAvatarIdx === idx
                    ? '3px solid #1e293b'
                    : '3px solid transparent',
                boxShadow:
                  selectedAvatarIdx === idx
                    ? '0 0 0 2px #6366f1'
                    : '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.15s',
                transform: selectedAvatarIdx === idx ? 'scale(1.1)' : 'scale(1)',
              }}
            >
              <Text
                style={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 20,
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                }}
              >
                {displayName.charAt(0).toUpperCase()}
              </Text>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
